import { useEffect, useRef, useState } from 'react';

import { Button } from 'components/ui/button';
import { getGoogleAuthConfig, loginWithGoogle } from './api';
import { GoogleAuthButtonProps, GoogleCodeClient, GoogleWindow } from './auth.types';
import { loadGoogleScript } from './load-google-script';

const GoogleIcon = () => {
  return (
    <svg viewBox='0 0 24 24' aria-hidden='true'>
      <path
        d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z'
        fill='#4285F4'
      />
      <path
        d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z'
        fill='#34A853'
      />
      <path
        d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z'
        fill='#FBBC05'
      />
      <path
        d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z'
        fill='#EA4335'
      />
    </svg>
  );
};

export const GoogleAuthButton = ({ disabled, onSuccess, onError }: GoogleAuthButtonProps) => {
  const codeClientRef = useRef<GoogleCodeClient | null>(null);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const [isReady, setIsReady] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  onSuccessRef.current = onSuccess;
  onErrorRef.current = onError;

  useEffect(() => {
    let isCancelled = false;

    const setup = async () => {
      try {
        const config = await getGoogleAuthConfig();
        if (!config.clientId || isCancelled) {
          return;
        }

        await loadGoogleScript();
        if (isCancelled) {
          return;
        }

        const googleWindow = window as GoogleWindow;
        const oauth2 = googleWindow.google?.accounts?.oauth2;
        if (!oauth2) {
          return;
        }

        codeClientRef.current = oauth2.initCodeClient({
          client_id: config.clientId,
          scope: 'openid email profile',
          ux_mode: 'popup',
          callback: async (response) => {
            if (!response.code) {
              onErrorRef.current('Google sign-in failed');

              return;
            }

            setIsGoogleLoading(true);
            try {
              const tokens = await loginWithGoogle(response.code);
              onSuccessRef.current(tokens);
            } catch (error) {
              if (error instanceof Error) {
                onErrorRef.current(error.message);

                return;
              }

              onErrorRef.current('Google sign-in failed');
            } finally {
              setIsGoogleLoading(false);
            }
          },
          error_callback: (error) => {
            if (error.type === 'popup_closed' || error.type === 'popup_closed_by_user') {
              return;
            }

            onErrorRef.current('Google sign-in failed');
          },
        });

        setIsReady(true);
      } catch {
        return;
      }
    };

    setup();

    return () => {
      isCancelled = true;
    };
  }, []);

  if (!isReady) {
    return null;
  }

  const handleClick = () => {
    codeClientRef.current?.requestCode();
  };

  return (
    <>
      <div className='relative'>
        <div className='absolute inset-0 flex items-center'>
          <span className='w-full border-t' />
        </div>
        <div className='relative flex justify-center text-xs uppercase'>
          <span className='bg-card px-2 text-muted-foreground'>or</span>
        </div>
      </div>
      <Button
        type='button'
        variant='outline'
        className='w-full'
        disabled={disabled || isGoogleLoading}
        onClick={handleClick}
      >
        <GoogleIcon />
        Continue with Google
      </Button>
    </>
  );
};
